/**
 * BOSS Reports — the report editor (the legacy designer, ported).
 *
 * A slide-over on the report page rather than a route of its own: editing a query is iterative —
 * probe the schema, save, watch it run, adjust — and a page swap costs the grid you are editing
 * against on every lap.
 *
 * It edits TWO things, and the difference matters because they are stored separately and have
 * different rules:
 *
 *  • The DEFINITION (`PUT /reports/{id}`) — name, memo, IsCore, the query or the report it is
 *    inherited from, and the field list: caption, data type, visibility, and each field's
 *    pre-filter. Gated on `boss.reports.design`.
 *  • The LAYOUT (`PUT /reports/{id}/layout`) — number formats and calculated fields, which are
 *    Perspective's own `columns_config` and `expressions`. The grid's right-click menu writes the
 *    same keys; this is the same layout everyone sees, not a private view.
 *
 * Save sends whichever of the two changed. Both are shared: there is one definition and one saved
 * layout per report, so the footer says so rather than leaving it to be discovered.
 *
 * The pre-filter DEFAULTS are not re-implemented here. `PreFilterDialog` already contains the
 * seven-tab date chooser, the store/supplier dual lists and the value list, so a field's defaults
 * are edited by opening that dialog scoped to the one field — the same trick the opening prompt
 * sequence uses.
 */
import { useCallback, useMemo, useState } from 'react'
import type {
    BossField, BossFieldWrite, BossPreFilterValue, BossReportDetail, BossReportSummary,
    BossReportWrite, BossRunResult, BossSchema, BossStoreOption,
} from '../../types/boss'
import type { BossDataType, BossDatePart, BossDateType, BossFilterType } from '../../types/boss'
import {
    calculatedFields, formatOf, referencesColumn, removeCalculatedField, removeColumn,
    setCalculatedField, setFormat, validateCalculatedField,
} from '../../lib/bossLayoutOps'
import type { BossFormat, ViewerConfig } from '../../lib/bossLayoutOps'
import { describePreFilter } from '../../lib/bossPreFilters'
import PreFilterDialog from './PreFilterDialog'

export type ReportEditorTab = 'report' | 'query' | 'fields' | 'calculated'

export type ReportEditorSave = {
    /** Null when nothing about the definition changed. */
    definition: BossReportWrite | null
    /** Null when no format or calculated field changed. */
    viewer: ViewerConfig | null
}

export type ReportEditorProps = {
    detail: BossReportDetail
    /** The viewer config on screen — what formats and calculated fields are read from and written to. */
    viewer: ViewerConfig
    /** The last run, for the calculated-field editor's column list. Null before the first run. */
    result?: BossRunResult | null
    /** Every report, for the "inherits its query from" picker. */
    reports?: BossReportSummary[]
    stores?: BossStoreOption[]
    suppliers?: string[]

    /** The probe result to show, and whether one is in flight. */
    schema?: BossSchema | null
    probing?: boolean
    schemaError?: string | null
    saving?: boolean
    saveError?: string | null

    onProbe: (sql: string, apply: boolean, destructive: boolean) => void
    onSave: (save: ReportEditorSave) => void
    onClose: () => void
}

const DATA_TYPES: BossDataType[] = ['string', 'integer', 'float', 'date', 'datetime', 'boolean']

const FILTER_TYPES: { value: BossFilterType; label: string }[] = [
    { value: 'value', label: 'Values' },
    { value: 'store', label: 'Stores' },
    { value: 'supplier', label: 'Suppliers' },
    { value: 'dateAbsolute', label: 'Date' },
    { value: 'dateRelative', label: 'Date (relative)' },
]

const FORMATS: { value: BossFormat; label: string }[] = [
    { value: 'auto', label: 'Auto' },
    { value: 'decimal', label: 'Decimal' },
    { value: 'currency', label: 'Currency' },
    { value: 'percent', label: 'Percent' },
]

/**
 * The shape a date pre-filter defaults to — the legacy DateChooserForm's tabs, which is also what
 * the "Default…" dialog offers. Shown on the row as well so the report's default date behaviour is
 * visible at a glance rather than one dialog deep.
 */
const DATE_TYPES: { value: BossDateType; label: string }[] = [
    { value: 'none', label: 'No default' },
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'season', label: 'Season' },
    { value: 'year', label: 'Year' },
    { value: 'custom', label: 'Custom range' },
    { value: 'relative', label: 'Relative' },
]

/**
 * The date parts a datetime column can also be displayed as, in the order the server lists them
 * (coarsest first, and a truncation beside the extraction it is most easily confused with).
 *
 * `month` is the month NAME ignoring the year — every January together — while `monthStart` is the
 * first of that month, so 2024 and 2025 stay apart. Same distinction for `hour` (0–23) and
 * `hourStart`. The labels say which is which, because picking the wrong one produces a report that
 * looks right and totals something else.
 */
const DATE_PARTS: { value: BossDatePart; label: string; hint: string }[] = [
    { value: 'year', label: 'Year', hint: 'The year' },
    { value: 'monthStart', label: 'Month start', hint: 'First of the month — years stay apart' },
    { value: 'month', label: 'Month', hint: 'Month name — every January together' },
    { value: 'week', label: 'Week', hint: 'Start of the week' },
    { value: 'date', label: 'Date', hint: 'The day, without the time' },
    { value: 'dayOfWeek', label: 'Day of week', hint: 'Monday, Tuesday…' },
    { value: 'hourStart', label: 'Hour start', hint: 'Start of the hour, on its own day' },
    { value: 'hour', label: 'Hour', hint: 'Hour of the day, 0–23' },
    { value: 'minute', label: 'Minute', hint: 'Start of the minute' },
    { value: 'second', label: 'Second', hint: 'Start of the second' },
]

const isDateColumn = (type: BossDataType): boolean => type === 'date' || type === 'datetime'

const DATE_PART_LABELS = new Map(DATE_PARTS.map(part => [part.value, part.label]))

/**
 * The grid column one part of one field becomes — `TRANSTIME (Year)`.
 *
 * The server builds the same string (`BossDateParts.ColumnName`), and a run result hands it back
 * ready-made. This exists because the editor must work with NO run: a report that has never run,
 * or whose SQL is broken and cannot, is exactly the report somebody opens this to fix, and every
 * guard keyed on `result.dateParts` would silently do nothing there.
 *
 * The two label tables are pinned against the same list on both sides — `DATE_PART_LABELS` here,
 * `BossDatePartsTests.LabelsAreTheOnesTheClientAlsoUses` there — so they cannot drift apart
 * unnoticed.
 */
function datePartColumnName(field: string, part: BossDatePart): string {
    return `${field} (${DATE_PART_LABELS.get(part) ?? part})`
}

/** The field write a stored field starts as, so an untouched editor sends back what it was given. */
function toWrite(field: BossField): BossFieldWrite {
    return {
        id: field.id, name: field.name, caption: field.caption, filterColumn: field.filterColumn,
        dataType: field.dataType, sortOrder: field.sortOrder,
        isFiltered: field.isFiltered, prompt: field.prompt, persistValues: field.persistValues,
        filterType: field.filterType, dateType: field.dateType,
        seasonCode: field.seasonCode, seasonYear: field.seasonYear,
        startDate: field.startDate, endDate: field.endDate,
        relativeRange: field.relativeRange, reoYears: field.reoYears,
        include: [...field.include], exclude: [...field.exclude],
        isHiddenForStores: field.isHiddenForStores, isHidden: field.isHidden,
        dateParts: [...field.dateParts],
    }
}

/**
 * A draft row seen as a `BossField`, so `PreFilterDialog` and `describePreFilter` can be handed the
 * field the designer is editing rather than the one that was stored. Changing a field to "Stores"
 * and opening its defaults must show the store list, not the one its saved type implies.
 */
function asField(write: BossFieldWrite): BossField {
    return { ...write, id: write.id ?? write.name, isOrphaned: false }
}

/** The value a field's stored defaults amount to, for the dialog to open on and the row to describe. */
function valueOf(write: BossFieldWrite): BossPreFilterValue {
    return {
        field: write.name,
        dateType: write.dateType,
        from: write.startDate ?? undefined,
        to: write.endDate ?? undefined,
        seasonCode: write.seasonCode ?? undefined,
        seasonYear: write.seasonYear ?? undefined,
        relativeRange: write.relativeRange ?? undefined,
        reoYears: write.reoYears,
        include: write.include,
        exclude: write.exclude,
    }
}

/** The dialog's answer written back onto the draft row. Only the value half; the flags are the row's. */
function withValue(write: BossFieldWrite, value: BossPreFilterValue | undefined): BossFieldWrite {
    return {
        ...write,
        dateType: value?.dateType ?? 'none',
        startDate: value?.from ?? null,
        endDate: value?.to ?? null,
        seasonCode: value?.seasonCode ?? null,
        seasonYear: value?.seasonYear ?? null,
        relativeRange: value?.relativeRange ?? null,
        reoYears: value?.reoYears ?? 0,
        include: value?.include ? [...value.include] : [],
        exclude: value?.exclude ? [...value.exclude] : [],
    }
}

const isDateFilter = (type: BossFilterType): boolean =>
    type === 'dateAbsolute' || type === 'dateRelative'

export default function ReportEditor({
    detail, viewer, result = null, reports = [], stores = [], suppliers = [],
    schema = null, probing = false, schemaError = null, saving = false, saveError = null,
    onProbe, onSave, onClose,
}: ReportEditorProps) {
    const [tab, setTab] = useState<ReportEditorTab>('query')

    // ── The definition draft ──────────────────────────────────────────
    const [name, setName] = useState(detail.report.name)
    const [memo, setMemo] = useState(detail.report.memo ?? '')
    const [isCore, setIsCore] = useState(detail.report.isCore)
    const [linkedReportId, setLinkedReportId] = useState(detail.report.linkedReportId ?? '')
    const [sql, setSql] = useState(detail.sql ?? '')
    const [fields, setFields] = useState<BossFieldWrite[]>(() => detail.fields.map(toWrite))

    // ── The layout draft ──────────────────────────────────────────────
    const [draftViewer, setDraftViewer] = useState<ViewerConfig>(viewer)
    const [viewerDirty, setViewerDirty] = useState(false)

    /** The field whose pre-filter defaults are being edited, by name; null when the dialog is shut. */
    const [editingDefaults, setEditingDefaults] = useState<string | null>(null)

    // ── Calculated-field editor ───────────────────────────────────────
    const [calcName, setCalcName] = useState('')
    const [calcExpression, setCalcExpression] = useState('')
    /** The calculated field being edited, by its ORIGINAL name; null when adding a new one. */
    const [calcEditing, setCalcEditing] = useState<string | null>(null)

    // A date part is an expression in the layout too, but it is the FIELDS tab's business — it is
    // generated from the definition and editing its formula here would be overwritten on the next
    // read. Only formulas a designer actually wrote are listed.
    /**
     * Every date-part column this report has anything to do with — the ones the last run offered,
     * AND the ones the draft declares right now.
     *
     * Both halves are needed. The run is authoritative for what EXISTS, but is absent whenever the
     * report has not run — a broken query is the commonest reason to open this editor — and stale
     * the moment a part is ticked. The draft is authoritative for what the report WILL offer, but
     * says nothing about a part being removed. The union is what must never be mistaken for a
     * designer's own formula.
     */
    const datePartNames = useMemo(() => {
        const names = new Set((result?.dateParts ?? []).map(column => column.name))
        for (const field of fields) {
            if (!isDateColumn(field.dataType)) continue
            for (const part of field.dateParts) names.add(datePartColumnName(field.name, part))
        }
        return names
    }, [result, fields])
    const calcList = useMemo(
        () => calculatedFields(draftViewer).filter(field => !datePartNames.has(field.name)),
        [draftViewer, datePartNames])
    const calcError = useMemo(
        () => (calcName.trim().length === 0 && calcExpression.trim().length === 0
            ? null
            : validateCalculatedField(
                draftViewer, result, calcName, calcExpression, calcEditing ?? undefined)),
        [draftViewer, result, calcName, calcExpression, calcEditing])

    const patchViewer = useCallback((next: ViewerConfig) => {
        setDraftViewer(next)
        setViewerDirty(true)
    }, [])

    const patchField = useCallback((fieldName: string, changes: Partial<BossFieldWrite>) => {
        setFields(prev => prev.map(f => (f.name === fieldName ? { ...f, ...changes } : f)))
    }, [])

    // ── Dirty tracking ────────────────────────────────────────────────

    const definition = useMemo<BossReportWrite>(() => ({
        name: name.trim(),
        // An empty editor means "this report has no query of its own" and the link supplies it,
        // which is what the server reads a null Sql as.
        sql: sql.trim().length > 0 ? sql : null,
        linkedReportId: linkedReportId === '' ? null : linkedReportId,
        isCore,
        memo: memo.trim().length > 0 ? memo.trim() : null,
        // Date parts are dropped here rather than when the type select changes, so a mis-click on
        // that select does not destroy a selection the user cannot get back. The server refuses a
        // part on a non-date column, so sending them would be a 400; clearing at the last moment
        // means the type on screen is the one being saved against.
        fields: fields.map((f, index) => ({
            ...f,
            sortOrder: index,
            dateParts: isDateColumn(f.dataType) ? f.dateParts : [],
        })),
    }), [name, sql, linkedReportId, isCore, memo, fields])

    const definitionDirty = useMemo(() => {
        const original: BossReportWrite = {
            name: detail.report.name,
            sql: detail.sql,
            linkedReportId: detail.report.linkedReportId,
            isCore: detail.report.isCore,
            memo: detail.report.memo,
            fields: detail.fields.map((f, index) => ({ ...toWrite(f), sortOrder: index })),
        }
        return JSON.stringify(original) !== JSON.stringify(definition)
    }, [detail, definition])

    const nameError = name.trim().length === 0
        ? 'A report name is required.'
        : name.trim().length > 200 ? 'A report name is at most 200 characters.' : null

    /**
     * The layout to save, with every column the designer has just hidden taken out of it.
     *
     * A hidden field is gone from the run's results, so a layout still naming it in Rows or Values
     * asks Perspective to draw a column the table does not have. The field bar tolerates that (it
     * drops what the result no longer has), but the config handed to the viewer is the stored one,
     * so it is fixed here — at the moment the column stops existing — rather than left for the
     * next reader to trip over.
     *
     * Null when nothing about the layout changed AND nothing was hidden: the two are independent,
     * and hiding a field alone is still a reason to rewrite the layout.
     */
    const viewerToSave = useMemo(() => {
        const byName = new Map(fields.map(f => [f.name, f]))

        // Every column this save takes away, whether the designer hid it or stopped offering it.
        //
        // A date part is the second, less obvious half. Its column is only computed while the
        // report OFFERS it, so unticking a part — or hiding the field it is computed from — makes
        // `FIELD (Year)` stop existing just as surely as hiding a column does. A layout still
        // naming it hands Perspective an expression over a column the result does not carry, and
        // Perspective refuses the WHOLE config for one bad expression: the grid then draws an
        // unconfigured table and nothing says why.
        //
        // The candidates are the parts the last run offered PLUS the parts the layout still names
        // from before — not just the former. `result` is null whenever the report has not run, and
        // a broken query is the commonest reason to be in this editor at all; keying only on the
        // run would make the whole strip a silent no-op exactly there.
        const candidates = new Map<string, { field: string; part: BossDatePart }>()
        for (const column of result?.dateParts ?? []) {
            candidates.set(column.name, { field: column.field, part: column.part })
        }
        for (const field of detail.fields) {
            for (const part of field.dateParts) {
                candidates.set(datePartColumnName(field.name, part), { field: field.name, part })
            }
        }

        const departing = [
            ...fields.filter(f => f.isHidden).map(f => f.name),
            ...[...candidates.entries()]
                .filter(([, column]) => {
                    const field = byName.get(column.field)
                    return !field || field.isHidden || !field.dateParts.includes(column.part)
                })
                .map(([name]) => name),
        ].filter(name => referencesColumn(draftViewer, name))

        if (!viewerDirty && departing.length === 0) return null
        return departing.reduce(removeColumn, draftViewer)
    }, [fields, detail.fields, draftViewer, viewerDirty, result])

    const dirty = definitionDirty || viewerToSave !== null

    const save = () => {
        if (nameError) return
        onSave({
            definition: definitionDirty ? definition : null,
            viewer: viewerToSave,
        })
    }

    // ── Calculated-field actions ──────────────────────────────────────

    const commitCalc = () => {
        if (calcError || calcName.trim().length === 0 || calcExpression.trim().length === 0) return
        patchViewer(setCalculatedField(
            draftViewer, calcName, calcExpression, calcEditing ?? undefined))
        setCalcName('')
        setCalcExpression('')
        setCalcEditing(null)
    }

    const startCalcEdit = (field: { name: string; expression: string }) => {
        setCalcEditing(field.name)
        setCalcName(field.name)
        setCalcExpression(field.expression)
    }

    const cancelCalcEdit = () => {
        setCalcEditing(null)
        setCalcName('')
        setCalcExpression('')
    }

    const editingField = editingDefaults === null
        ? null
        : fields.find(f => f.name === editingDefaults) ?? null

    // Dimming modal: deliberately no backdrop onClick — dialogs must not close on outside-click.
    return (
        <div className="modal-backdrop">
            <div className="boss-editor">
                <div className="modal-header">
                    <h2>Edit “{detail.report.name}”</h2>
                    <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="boss-tabs" role="tablist">
                    {([
                        ['query', 'Query'],
                        ['fields', `Fields (${fields.length})`],
                        ['calculated', `Calculated (${calcList.length})`],
                        ['report', 'Report'],
                    ] as [ReportEditorTab, string][]).map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={tab === key}
                            className={`boss-tab${tab === key ? ' active' : ''}`}
                            onClick={() => setTab(key)}
                        >{label}</button>
                    ))}
                </div>

                <div className="boss-editor-body">
                    {tab === 'report' && (
                        <div className="boss-editor-form">
                            <label className="form-field">
                                <span className="form-label">Name</span>
                                <input
                                    className="form-input"
                                    maxLength={200}
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                />
                                {nameError && <div className="boss-error">{nameError}</div>}
                            </label>

                            <label className="form-field">
                                <span className="form-label">Memo</span>
                                <textarea
                                    className="form-input"
                                    rows={3}
                                    value={memo}
                                    onChange={e => setMemo(e.target.value)}
                                />
                            </label>

                            <label className="boss-check">
                                <input
                                    type="checkbox"
                                    checked={isCore}
                                    onChange={e => setIsCore(e.target.checked)}
                                />
                                Core report
                            </label>

                            <label className="form-field">
                                <span className="form-label">Inherits its query from</span>
                                <select
                                    className="form-select"
                                    value={linkedReportId}
                                    onChange={e => setLinkedReportId(e.target.value)}
                                >
                                    <option value="">Nothing — this report owns its query</option>
                                    {reports
                                        .filter(r => r.id !== detail.report.id)
                                        .map(r => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                </select>
                                <div className="boss-editor-hint">
                                    A linked report runs its source's query. The chain is flattened
                                    to the report that owns SQL, and may not loop back here.
                                </div>
                            </label>
                        </div>
                    )}

                    {tab === 'query' && (
                        <div className="boss-editor-form">
                            {detail.report.needsReview && (
                                <div className="boss-error">
                                    This report cannot run until it is fixed:{' '}
                                    {detail.report.needsReviewReason}
                                </div>
                            )}
                            <label className="form-field">
                                <span className="form-label">SQL</span>
                                <textarea
                                    className="form-input boss-sql-editor"
                                    spellCheck={false}
                                    rows={18}
                                    value={sql}
                                    placeholder={linkedReportId
                                        ? 'Empty — this report inherits its query.'
                                        : 'SELECT …'}
                                    onChange={e => setSql(e.target.value)}
                                />
                                <div className="boss-editor-hint">
                                    Read-only queries against the head-office database. Parameter
                                    names beginning <code>@boss_</code> are reserved. Probing runs
                                    the query with no rows returned, to read its columns.
                                </div>
                            </label>

                            <div className="boss-editor-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    disabled={probing || sql.trim().length === 0}
                                    onClick={() => onProbe(sql, false, false)}
                                >{probing ? 'Probing…' : 'Probe schema'}</button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    disabled={probing || sql.trim().length === 0}
                                    title="Add columns the query now returns and flag the ones it no longer does"
                                    onClick={() => onProbe(sql, true, false)}
                                >Recover fields</button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    disabled={probing || sql.trim().length === 0}
                                    title="As Recover, and delete the fields the query no longer returns"
                                    onClick={() => onProbe(sql, true, true)}
                                >Regenerate fields</button>
                            </div>

                            {schemaError && <div className="boss-error">{schemaError}</div>}

                            {schema && (
                                <div className="boss-schema">
                                    <div className="boss-editor-hint">
                                        {schema.columns.length} column(s) returned.
                                        {/* Recover and Regenerate write the field list on the
                                            SERVER. This editor is holding a draft made before that
                                            happened, so it says where the truth now is rather than
                                            silently showing stale rows on the Fields tab. */}
                                        {schema.applied && (
                                            ' The field list has been reconciled — close and reopen'
                                            + ' the editor to work on it.')}
                                    </div>
                                    <ul className="boss-schema-list">
                                        {schema.columns.map(column => (
                                            <li key={column.name}>
                                                <code>{column.name}</code>
                                                <span className="boss-muted">{column.dataType}</span>
                                                {column.status === 'new' && (
                                                    <span className="boss-badge boss-badge-core">new</span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                    {schema.orphanedFields.length > 0 && (
                                        <div className="boss-error">
                                            No longer returned: {schema.orphanedFields.join(', ')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'fields' && (
                        <div className="boss-field-rows">
                            {fields.length === 0 && (
                                <div className="boss-empty">
                                    No fields yet — probe the query to discover its columns.
                                </div>
                            )}
                            {fields.map(field => {
                                const stored = detail.fields.find(f => f.name === field.name)
                                const value = valueOf(field)
                                return (
                                    <div
                                        key={field.name}
                                        className={`boss-field-row${stored?.isOrphaned ? ' orphaned' : ''}`}
                                    >
                                        <div className="boss-field-head">
                                            <code className="boss-field-name">{field.name}</code>
                                            {stored?.isOrphaned && (
                                                <span className="boss-badge boss-badge-review">
                                                    not returned
                                                </span>
                                            )}
                                            <input
                                                className="form-input"
                                                placeholder="Caption"
                                                aria-label={`Caption for ${field.name}`}
                                                value={field.caption ?? ''}
                                                onChange={e => patchField(field.name, {
                                                    caption: e.target.value === '' ? null : e.target.value,
                                                })}
                                            />
                                            <select
                                                className="form-select"
                                                aria-label={`Data type for ${field.name}`}
                                                value={field.dataType}
                                                // The parts are NOT cleared here. A part is only
                                                // meaningful on a datetime column and the server
                                                // refuses one anywhere else — but the row simply
                                                // stops showing them, and dropping the ticks on a
                                                // mis-click would destroy a selection with no undo
                                                // short of abandoning every other edit in the
                                                // draft. `definition` clears them at SAVE instead,
                                                // by which point the type is the one being kept.
                                                onChange={e => patchField(field.name, {
                                                    dataType: e.target.value as BossDataType,
                                                })}
                                            >
                                                {DATA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                            <select
                                                className="form-select"
                                                aria-label={`Format for ${field.name}`}
                                                value={formatOf(draftViewer, field.name)}
                                                onChange={e => patchViewer(setFormat(
                                                    draftViewer, field.name, e.target.value as BossFormat))}
                                            >
                                                {FORMATS.map(f => (
                                                    <option key={f.value} value={f.value}>{f.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="boss-field-flags">
                                            <label className="boss-check" title="Uncheck to drop this column from the report's results for everyone">
                                                <input
                                                    type="checkbox"
                                                    checked={!field.isHidden}
                                                    onChange={e => patchField(field.name, {
                                                        isHidden: !e.target.checked,
                                                    })}
                                                />
                                                Shown
                                            </label>
                                            <label className="boss-check" title="Head office keeps this column; store-level viewers do not see it">
                                                <input
                                                    type="checkbox"
                                                    checked={field.isHiddenForStores}
                                                    onChange={e => patchField(field.name, {
                                                        isHiddenForStores: e.target.checked,
                                                    })}
                                                />
                                                Head office only
                                            </label>
                                            <label className="boss-check">
                                                <input
                                                    type="checkbox"
                                                    checked={field.isFiltered}
                                                    onChange={e => patchField(field.name, {
                                                        isFiltered: e.target.checked,
                                                        // A field that is not a pre-filter has no
                                                        // filter kind, and leaving a stale one
                                                        // behind would resurrect it on the next tick.
                                                        filterType: e.target.checked
                                                            ? (field.filterType === 'none' ? 'value' : field.filterType)
                                                            : 'none',
                                                        prompt: e.target.checked && field.prompt,
                                                    })}
                                                />
                                                Pre-filter
                                            </label>

                                            {field.isFiltered && (
                                                <>
                                                    <select
                                                        className="form-select"
                                                        aria-label={`Filter kind for ${field.name}`}
                                                        value={field.filterType}
                                                        onChange={e => {
                                                            const filterType = e.target.value as BossFilterType
                                                            patchField(field.name, {
                                                                filterType,
                                                                // Switching between a date and a list
                                                                // changes which half of the value is
                                                                // meaningful; the other half is dropped
                                                                // rather than left to be sent as noise.
                                                                dateType: isDateFilter(filterType)
                                                                    ? (field.dateType === 'none' ? 'day' : field.dateType)
                                                                    : 'none',
                                                                include: isDateFilter(filterType) ? [] : field.include,
                                                                exclude: isDateFilter(filterType) ? [] : field.exclude,
                                                            })
                                                        }}
                                                    >
                                                        {FILTER_TYPES.map(t => (
                                                            <option key={t.value} value={t.value}>{t.label}</option>
                                                        ))}
                                                    </select>
                                                    {isDateFilter(field.filterType) && (
                                                        <select
                                                            className="form-select"
                                                            aria-label={`Default date shape for ${field.name}`}
                                                            title="The date shape this report's filter starts on — the same choice as the tabs in Default…"
                                                            value={field.dateType}
                                                            onChange={e => patchField(field.name, {
                                                                dateType: e.target.value as BossDateType,
                                                            })}
                                                        >
                                                            {DATE_TYPES.map(t => (
                                                                <option key={t.value} value={t.value}>{t.label}</option>
                                                            ))}
                                                        </select>
                                                    )}
                                                    <label className="boss-check" title="Ask the viewer for this value before the report runs">
                                                        <input
                                                            type="checkbox"
                                                            checked={field.prompt}
                                                            onChange={e => patchField(field.name, {
                                                                prompt: e.target.checked,
                                                            })}
                                                        />
                                                        Prompt
                                                    </label>
                                                    <label className="boss-check" title="Keep what the viewer chooses as this field's new default">
                                                        <input
                                                            type="checkbox"
                                                            checked={field.persistValues}
                                                            onChange={e => patchField(field.name, {
                                                                persistValues: e.target.checked,
                                                            })}
                                                        />
                                                        Persist
                                                    </label>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary boss-btn-sm"
                                                        onClick={() => setEditingDefaults(field.name)}
                                                    >Default…</button>
                                                    <span className="boss-field-default">
                                                        {describePreFilter(asField(field), value)}
                                                    </span>
                                                </>
                                            )}
                                        </div>

                                        {/* Date parts. A third line, and only on a date column:
                                            ticking several is the point — one field becomes several
                                            grid columns, TRANSTIME as a year beside TRANSTIME as an
                                            hour, each grouped and sorted on its own. The field
                                            itself stays available in its own right. */}
                                        {isDateColumn(field.dataType) && (
                                            <div className="boss-field-parts">
                                                <span className="boss-field-parts-label">
                                                    Also show as
                                                </span>
                                                {DATE_PARTS.map(part => (
                                                    <label
                                                        key={part.value}
                                                        className="boss-check"
                                                        title={part.hint}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={field.dateParts.includes(part.value)}
                                                            // Kept in the vocabulary's own order, not
                                                            // in click order: nothing downstream
                                                            // cares, but the dirty check is a
                                                            // JSON compare, so appending would make
                                                            // untick-then-retick look like an edit
                                                            // and offer to rewrite the whole report
                                                            // over a reordering with no effect.
                                                            onChange={e => patchField(field.name, {
                                                                dateParts: DATE_PARTS
                                                                    .map(p => p.value)
                                                                    .filter(p => (p === part.value
                                                                        ? e.target.checked
                                                                        : field.dateParts.includes(p))),
                                                            })}
                                                        />
                                                        {part.label}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {tab === 'calculated' && (
                        <div className="boss-editor-form">
                            <div className="boss-editor-hint">
                                A calculated field is a Perspective formula over this report's
                                columns, computed as the grid draws — so it is right at every
                                subtotal level, which a ratio computed in SQL is not. Reference a
                                column by name in double quotes, e.g.{' '}
                                <code>("RETAIL" - "COST") / "RETAIL"</code>. It is a measure: it can
                                sit in Values, never in Rows or Columns.
                            </div>

                            {calcList.length === 0 && (
                                <div className="boss-empty">No calculated fields yet.</div>
                            )}
                            {calcList.map(field => (
                                <div key={field.name} className="boss-calc-row">
                                    <code className="boss-field-name">{field.name}</code>
                                    <code className="boss-calc-expression">{field.expression}</code>
                                    <button
                                        type="button"
                                        className="btn btn-secondary boss-btn-sm"
                                        onClick={() => startCalcEdit(field)}
                                    >Edit</button>
                                    <button
                                        type="button"
                                        className="btn btn-danger boss-btn-sm"
                                        onClick={() => {
                                            patchViewer(removeCalculatedField(draftViewer, field.name))
                                            if (calcEditing === field.name) cancelCalcEdit()
                                        }}
                                    >Remove</button>
                                </div>
                            ))}

                            <div className="boss-calc-form">
                                <label className="form-field">
                                    <span className="form-label">Name</span>
                                    <input
                                        className="form-input"
                                        placeholder="MARGIN"
                                        value={calcName}
                                        onChange={e => setCalcName(e.target.value)}
                                    />
                                </label>
                                <label className="form-field">
                                    <span className="form-label">Formula</span>
                                    <textarea
                                        className="form-input boss-sql-editor"
                                        spellCheck={false}
                                        rows={3}
                                        placeholder={'("RETAIL" - "COST") / "RETAIL"'}
                                        value={calcExpression}
                                        onChange={e => setCalcExpression(e.target.value)}
                                    />
                                </label>
                                {result && (
                                    <div className="boss-editor-hint">
                                        Columns: {Object.keys(result.schema).map(c => `"${c}"`).join(', ')}
                                    </div>
                                )}
                                {calcError && <div className="boss-error">{calcError}</div>}
                                <div className="boss-editor-actions">
                                    {calcEditing !== null && (
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={cancelCalcEdit}
                                        >Cancel edit</button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={calcError !== null
                                            || calcName.trim().length === 0
                                            || calcExpression.trim().length === 0}
                                        onClick={commitCalc}
                                    >{calcEditing === null ? 'Add field' : 'Save field'}</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <div className="boss-editor-foot">
                        <span className="boss-muted">
                            {dirty
                                ? 'Saving replaces this report for everyone who opens it.'
                                : 'No changes yet.'}
                        </span>
                        {saveError && <span className="boss-error">{saveError}</span>}
                        <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ marginLeft: 'auto' }}
                            onClick={onClose}
                        >Close</button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={!dirty || saving || nameError !== null}
                            onClick={save}
                        >{saving ? 'Saving…' : 'Save'}</button>
                    </div>
                </div>
            </div>

            {editingField && (
                <PreFilterDialog
                    fields={[asField(editingField)]}
                    values={[valueOf(editingField)]}
                    stores={stores}
                    suppliers={suppliers}
                    canSaveDefaults={false}
                    title={`Default for ${editingField.name}`}
                    caption={
                        'This is the value the report starts with. A viewer can change it for their '
                        + 'own run; only saving here changes what the report opens with.'}
                    applyLabel="Set default"
                    today={detail.businessDate}
                    businessTimeZone={detail.businessTimeZone}
                    onApply={values => {
                        patchField(
                            editingField.name,
                            withValue(editingField, values.find(v => v.field === editingField.name)))
                        setEditingDefaults(null)
                    }}
                    onCancel={() => setEditingDefaults(null)}
                />
            )}
        </div>
    )
}
